import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  TextField,
  Divider,
} from "@mui/material";
import { RemoveCircle, AddCircle, Delete } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store/type";
import {
  updateCartItemQuantity,
  removeFromCart,
  EmptyCart,
} from "@/store/authSlice";
import { useNavigate } from "react-router-dom";
import { isUndefined } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { showToast } from "@/app/global-components/toaster";
import { routes } from "@/consts";
import useOrder from "@/hooks/use-order";

const CheckoutPage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const cart = useSelector((state: RootState) => state.auth.cart);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { createOrder } = useOrder();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const handleQuantityChange = (id: string, quantity: number) => {
    if (quantity < 1) return;
    dispatch(updateCartItemQuantity({ id, quantity }));
  };

  const handleRemoveItem = (productId: string) => {
    dispatch(removeFromCart(productId));
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      showToast(t("Alert.CartEmpty"), "warning");
      return;
    }

    setIsPlacingOrder(true);
    const orderData = {
      title: "Order for Tender", // Example title
      endDate: new Date().toISOString(), // Example end date
      deliverDate: new Date(
        new Date().getTime() + 7 * 24 * 60 * 60 * 1000
      ).toISOString(), // Deliver in 7 days
      type: "Tiny",
      status: "Planning",
      region: myCompany.region,
      city: myCompany.city,
      address: myCompany.address,
      products: cart.map((item) => ({
        itemId: item.productId,
        item: item.unitName,
        SKUCode: item.SKUCode || "",
        quantity: item.quantity,
        discount: 0,
        price: item.unitPrice,
        vat: item.vat || 15,
        notice: item.notice || "",
        image: item.image,
        companyId: item.company.id,
        tender: item.tender.id,
        quatationId: item.quatationId,
      })),
      tenderId: cart[0].tender.id,
      userId: authUser?.id,
      companyId: myCompany?.id,
      Sendedproducts: [],
      attachmentRequired: false,
      OrderNr: myCompany ? myCompany.orderNr + 1 : 1,
    };
    try {
      const response = await createOrder(orderData); // Assuming createOrder is an API call to save the order
      if (response?.isSuccess) {
        showToast(t("Alert.OrderCreated"), "success");
        dispatch(EmptyCart());
        navigate(`${routes.order}/${response.data.id}`); // Navigate to the created order's page
      } else {
        showToast(t("Alert.OrderCreationError"), "warning");
      }
    } catch (error) {
      showToast(t("Alert.OrderTryAgain"), "warning");
      console.error("Error creating order:", error);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const calculateTotal = () =>
    cart.reduce(
      (acc, item) =>
        acc +
        item.unitPrice * item.quantity +
        (item.unitPrice *
          item.quantity *
          (isUndefined(item.vat) ? 15 : item.vat)) /
          100,
      0
    );

  const calculateTotalVAT = () =>
    cart.reduce(
      (acc, item) =>
        acc +
        (item.unitPrice *
          item.quantity *
          (isUndefined(item.vat) ? 15 : item.vat)) /
          100,
      0
    );

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        {t("checkout.reviewCart")}
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {cart.length > 0 ? (
            cart.map((item) => {
              return (
                <Card
                  key={item.productId}
                  sx={{
                    display: "flex",
                    marginBottom: 2,
                    alignItems: "center",
                    padding: 2,
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{ width: 100, height: 100, borderRadius: 2 }}
                    image={item.image || "/placeholder-image.png"}
                    alt={item.unitName}
                  />
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h6">{item.unitName}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t("checkout.fields.company")}: {item.company.CompanyNr}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t("item.fields.price")}: {item.unitPrice.toFixed(2)}{" "}
                      {t("checkout.summary.currency")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t("checkout.fields.vat")}:{" "}
                      {isUndefined(item.vat) ? 15 : item.vat}%
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        marginTop: 2,
                      }}
                    >
                      <Typography variant="body2">
                        {t("checkout.fields.quantity")}:
                      </Typography>
                      <IconButton
                        color="primary"
                        onClick={() =>
                          handleQuantityChange(
                            item.productId,
                            item.quantity - 1
                          )
                        }
                      >
                        <RemoveCircle />
                      </IconButton>
                      <TextField
                        value={item.quantity}
                        size="small"
                        variant="outlined"
                        inputProps={{
                          style: { textAlign: "center", width: 40 },
                          min: 1,
                        }}
                        onChange={(e) =>
                          handleQuantityChange(
                            item.productId,
                            parseInt(e.target.value) || 1
                          )
                        }
                      />
                      <IconButton
                        color="primary"
                        onClick={() =>
                          handleQuantityChange(
                            item.productId,
                            item.quantity + 1
                          )
                        }
                      >
                        <AddCircle />
                      </IconButton>
                    </Box>
                  </CardContent>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveItem(item.productId)}
                  >
                    <Delete />
                  </IconButton>
                </Card>
              );
            })
          ) : (
            <Typography align="center" variant="h6" color="textSecondary">
              {t("checkout.messages.emptyCart")}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ padding: 2 }}>
            <Typography variant="h5" gutterBottom>
              {t("checkout.summary.title")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="textSecondary">
              {t("checkout.summary.totalPrice")}:{" "}
              <strong>
                {cart
                  .reduce(
                    (acc, item) => acc + item.unitPrice * item.quantity,
                    0
                  )
                  .toFixed(2)}{" "}
                {t("checkout.summary.currency")}
              </strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("checkout.summary.totalVat")}:{" "}
              <strong>
                {calculateTotalVAT().toFixed(2)}{" "}
                {t("checkout.summary.currency")}
              </strong>
            </Typography>
            <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
              {t("checkout.summary.grandTotal")}: {calculateTotal().toFixed(2)}{" "}
              {t("checkout.summary.currency")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handlePlaceOrder}
              disabled={cart.length === 0 || isPlacingOrder}
              sx={{
                backgroundColor: "#644980",
                "&:hover": {
                  backgroundColor: "#50376d",
                },
              }}
            >
              {isPlacingOrder
                ? t("actions.creatingOrder")
                : t("actions.createOrder")}
            </Button>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CheckoutPage;
